import { useConfirm, useLoading } from '@genial/components';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { OfferRequest, RequestedOfferDetailRequest } from '@genial/services/dist/models';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../../routes/types';
import { useBatchTicketActions } from '../../../store/batchTicket/actions';
import {
  basketCustomerOrderResumeState,
  customerCartStatusState,
  productsCartState,
  productsCartStatusState,
} from '../../../store/batchTicket/atom';
import { AssetSelectedTypeMap, SubmitUpdateProps } from '../../../store/product/types';
import { errorMessageBatchTicketRequestItem } from '../../BatchTicketRequests/utils';
import {
  OnlyBatchTicketProducts,
  propertiesMap,
} from '../components/DynamicButton/components/BatchTicketButton/constants';
import {
  cancelAdvisorBasketService,
  deleteCustomerOfferByBasketService,
  deleteProductBasketService,
  putCustomerOfferByBasketService,
  putProductBasketService,
} from '../services';
import { errorTotalValueProductMessage, sucessUpdateProductMessage } from '../utils';
import {
  AddedProductItemRouteParamTypeProps,
  PutCustomerOfferByBasketServiceProps,
  PutProductBasketServiceProps,
  UpdateProductCartOfferParams,
} from '../utils/types';

export interface useBatchTicketCartActionItemProps<T extends keyof AssetSelectedTypeMap> {
  item: AssetSelectedTypeMap[T];
  id: T;
  idSelector: keyof AssetSelectedTypeMap[T];
  type: AddedProductItemRouteParamTypeProps;
}

type NormalizeUpdateItemDataProps = Partial<{
  basketAdvisorOrderId: string;
  offer: OfferRequest;
  basketCustomerOfferId: string;
  billingTypeId: number;
  price: number;
  amount: number;
  requestedOfferDetail: Partial<RequestedOfferDetailRequest>;
}>;

export const useBatchTicketCartActionItem = <T extends keyof AssetSelectedTypeMap>(
  props: useBatchTicketCartActionItemProps<T>
) => {
  const { item, id, idSelector, type } = props;
  const { goBack } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });
  const { hide, show } = useLoading();
  const { confirm } = useConfirm();

  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const {
    removeProductToCart,
    removeCustomerOfferByBasketOrderResume,
    removeCustomerToBasketOrderResume,
    updateCustomerOfferInBasketOrderResume,
    updateProductItemCart,
  } = useBatchTicketActions();
  const { showToast, showToastFromError } = useToastCreator('toast-remove-offer-basket');

  const resetBasketCustomerOrderResume = useResetRecoilState(basketCustomerOrderResumeState);
  const resetCustomerCartStatus = useResetRecoilState(customerCartStatusState);
  const resetProductsCartStatus = useResetRecoilState(productsCartStatusState);

  const productsCart = useRecoilValue(productsCartState);
  const {
    data: basketCustomerOrderResumeData,
    summary: { basketAdvisorOrderId },
  } = useRecoilValue(basketCustomerOrderResumeState);

  const basketCustomerOrderId = item?.basketCustomerOrderId;

  const customerBasketSelected = basketCustomerOrderResumeData.all?.find(
    (customerBasket) => customerBasket?.basketCustomerOrderId! === basketCustomerOrderId
  );

  const countCustomerOffersBasket = customerBasketSelected?.customerOffers?.reduce(
    (acc, current) => {
      const basketOffers = current?.basketOffers || [];

      const { validOffersCount, invalidOffersCount } = basketOffers.reduce(
        (counts, offer) => {
          if (offer?.errors && offer?.errors?.length > 0) {
            counts.invalidOffersCount++;
          } else {
            counts.validOffersCount++;
          }
          return counts;
        },
        { validOffersCount: 0, invalidOffersCount: 0 }
      );

      return {
        validOffersCount: acc.validOffersCount + validOffersCount,
        invalidOffersCount: acc.invalidOffersCount + invalidOffersCount,
      };
    },
    { validOffersCount: 0, invalidOffersCount: 0 }
  ) || { validOffersCount: 0, invalidOffersCount: 0 };

  const countTotalCustomerOffersBasket =
    countCustomerOffersBasket.invalidOffersCount + countCustomerOffersBasket.validOffersCount;

  const isLastOfferInCustomerBasket = countTotalCustomerOffersBasket <= 1;
  const basketCustomerOrderResumeDataRemainsOneOrEmpty =
    basketCustomerOrderResumeData.all?.length <= 1;

  const handleRemoveProductCart = async () => {
    try {
      setIsLoadingAction(true);

      const offer = productsCart[id].items.find(
        (product) => product[idSelector] === item[idSelector]
      );

      await deleteProductBasketService({ basketAdvisorOfferId: offer?.basketOfferId! });
      removeProductToCart(id, offer!, idSelector);
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const onConfirmRemoveProductValidation = async () => {
    try {
      if (isLastOfferInCustomerBasket) {
        show({
          title: 'Aguarde estamos removendo a oferta e o cliente da sua cesta.',
          description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
        });
      } else {
        setIsLoadingAction(true);
      }

      const basketCustomerOfferId = item?.basketCustomerOfferId!;

      const itemToRemove = { basketCustomerOfferId };

      await deleteCustomerOfferByBasketService(itemToRemove);
      removeCustomerOfferByBasketOrderResume(itemToRemove);

      if (countTotalCustomerOffersBasket === 1) {
        goBack();
        await removeCustomerToBasketOrderResume(item.basketCustomerOrderId!);
        return;
      }

      if (
        (countCustomerOffersBasket.invalidOffersCount === 1 && type === 'requiredAttention') ||
        (countCustomerOffersBasket.validOffersCount === 1 && type === 'totalSummary')
      ) {
        goBack();
      }

      if (basketCustomerOrderResumeDataRemainsOneOrEmpty && isLastOfferInCustomerBasket) {
        show({
          title: 'Aguarde estamos cancelando sua cesta.',
          description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
        });
        await cancelAdvisorBasketService(basketAdvisorOrderId);
        resetBasketCustomerOrderResume();
        resetCustomerCartStatus();
        resetProductsCartStatus();
        goBack();
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
      setIsLoadingAction(false);
    }
  };

  const handleRemoveProductValidated = async () => {
    if (countTotalCustomerOffersBasket > 1) {
      await onConfirmRemoveProductValidation();
    } else {
      const confirmDescription = basketCustomerOrderResumeDataRemainsOneOrEmpty
        ? 'Esta ação removerá o produto, o único cliente da lista e cancelará a boleta.'
        : 'Esta ação removerá este produto e o cliente.';

      confirm(
        {
          isMobile: isMobile || isTablet,
          description: `${confirmDescription}\nDeseja continuar mesmo assim?`,
          title: 'Atenção',
          icon: 'Warning',
          iconColor: 'feedback.attention.default',
          confirmText: 'Continuar',
          confirmColor: 'feedback.error.default',
        },
        async () => await onConfirmRemoveProductValidation()
      );
    }
  };

  const normalizeUpdateDate = (data: NormalizeUpdateItemDataProps) => {
    const baseItem = {
      ...data,
      productId: item[idSelector] as string,
    };

    const equityProperties = id === 'equity' && {
      orderValue: data.price,
      orderQuantity: data.amount,
      billingTypeId: data.billingTypeId,
    };

    const treasuryProperties = id === 'directTreasury' && {
      orderValue: data.amount,
      orderQuantity: data.price,
    };

    const specificProperties = propertiesMap[id as OnlyBatchTicketProducts](
      {
        ...item,
        ...(data.amount
          ? {
              orderValue: data.amount,
            }
          : {}),
        ...(equityProperties ?? {}),
        ...(treasuryProperties ?? {}),
      } as any,
      data.requestedOfferDetail
    );

    return {
      ...baseItem,
      ...specificProperties,
    };
  };

  const handleUpdateProductCart = async ({
    amount,
    price,
    requestedOfferDetail,
  }: SubmitUpdateProps): Promise<boolean> => {
    setIsLoadingAction(true);

    try {
      const offer = productsCart[id].items.find(
        (product) => product[idSelector] === item[idSelector]
      );

      if (amount && price) {
        const totalPrice = amount * price;
        const totalPriceIsInvalid = totalPrice === 0 || totalPrice > 1000000;

        if (totalPriceIsInvalid) {
          showToast(...errorTotalValueProductMessage);
          return false;
        }
      }

      const updateProductItemData = normalizeUpdateDate({
        basketAdvisorOrderId: offer?.basketOfferId!,
        offer: offer as unknown as OfferRequest,
        amount,
        price,
        requestedOfferDetail,
      });

      await putProductBasketService(
        offer?.basketOfferId!,
        updateProductItemData as PutProductBasketServiceProps
      );
      showToast(...sucessUpdateProductMessage);

      updateProductItemCart({
        basketOfferId: offer?.basketOfferId!,
        ...updateProductItemData,
      } as unknown as UpdateProductCartOfferParams);

      return true;
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
      } else {
        showToast(...errorMessageBatchTicketRequestItem);
      }

      return false;
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleUpdateProductValidated = async ({ amount, price }: SubmitUpdateProps) => {
    setIsLoadingAction(true);

    try {
      const updateProductItemData = normalizeUpdateDate({
        basketCustomerOfferId: item?.basketCustomerOfferId!,
        amount,
        price,
        ...(id === 'equity' && {
          billingTypeId: 0,
        }),
      });

      await putCustomerOfferByBasketService(
        item?.basketCustomerOfferId!,
        updateProductItemData as PutCustomerOfferByBasketServiceProps
      );

      updateCustomerOfferInBasketOrderResume({
        basketCustomerOfferId: updateProductItemData.basketCustomerOfferId!,
        ...updateProductItemData,
      });

      showToast(...sucessUpdateProductMessage);

      return true;
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
      } else {
        showToast(...errorMessageBatchTicketRequestItem);
      }
      return false;
    } finally {
      setIsLoadingAction(false);
    }
  };

  const removeProductFunctions = {
    cart: handleRemoveProductCart,
    requiredAttention: handleRemoveProductValidated,
    totalSummary: handleRemoveProductValidated,
  };

  const updateProductFunctions = {
    cart: handleUpdateProductCart,
    requiredAttention: handleUpdateProductValidated,
    totalSummary: handleUpdateProductValidated,
  };

  const onRemoveProduct = removeProductFunctions[type || 'cart'];
  const onUpdateProduct = updateProductFunctions[type || 'cart'];

  return {
    isLoadingAction,
    onRemoveProduct,
    onUpdateProduct,
  };
};
